import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that renders a paginated list of items.
 *
 * ARVIMO perf (lug 2026):
 * - rootMargin 100px -> 1000px: il batch successivo parte ben prima che l'utente raggiunga il fondo.
 * - Lock di rendering per direzione + dedup dei fetch in volo: mai richieste duplicate.
 * - AbortController + generation counter: un cambio filtri/ordinamento annulla i fetch in volo e
 *   invalida le risposte in ritardo (fix race: la cache pagine veniva ripopolata con HTML pre-filtro).
 * - Skeleton a dimensione fissa (zero CLS) mentre un batch viene scaricato; stato aria-live dopo l'append.
 * - Dopo ogni append il sentinel viene ri-osservato: con batch da 12 puo' restare dentro il rootMargin
 *   e senza re-observe la catena si fermerebbe.
 * - ARVIMO UX: invito "Ehi, troppo scroll? Aiutati con i filtri" dopo N prodotti caricati
 *   (soglia dal setting di sezione, markup in product-grid.liquid, ref scrollHint).
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement} [paginationStatus] - Live region annunci a11y.
 * @property {HTMLElement} [scrollHint] - Invito ai filtri dopo molto scroll.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper | undefined} */
  #aspectRatioHelper;

  /** @type {Map<number, Promise<void>>} */
  #inflightFetches = new Map();

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** Invalidazione risposte in ritardo dopo un cambio filtri/sort */
  #generation = 0;

  #renderingNext = false;
  #renderingPrevious = false;

  /** ARVIMO UX: mostrato al massimo una volta per pageview */
  #scrollHintShown = false;

  /** @type {HTMLElement[]} */
  #skeletons = [];

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    this.#abortController.abort();
    // Remove the filter update listeners
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          // ARVIMO perf: preload anticipato (~1000px prima del fondo)
          rootMargin: '1000px 0px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * Forza una ri-valutazione del sentinel (l'observe iniziale genera sempre una entry):
   * serve dopo un append (sentinel ancora dentro il rootMargin) e come retry dopo un errore.
   * @param {"previous" | "next"} type
   * @param {number} [delay]
   */
  #rearmSentinel(type, delay = 0) {
    setTimeout(() => {
      if (!this.isConnected || !this.infinityScrollObserver) return;
      const sentinel = type === 'next' ? this.refs.viewMoreNext : this.refs.viewMorePrevious;
      if (sentinel) {
        this.infinityScrollObserver.unobserve(sentinel);
        this.infinityScrollObserver.observe(sentinel);
      }
    }, delay);
  }

  #showSkeletons() {
    const { grid } = this.refs;
    if (!grid || this.#skeletons.length) return;
    for (let i = 0; i < 2; i++) {
      const item = document.createElement('li');
      item.className = 'product-grid__item product-grid__skeleton';
      item.setAttribute('aria-hidden', 'true');
      this.#skeletons.push(item);
    }
    grid.append(...this.#skeletons);
  }

  #hideSkeletons() {
    for (const item of this.#skeletons) item.remove();
    this.#skeletons = [];
  }

  /**
   * Annuncio per screen reader dopo un append (regione aria-live in product-grid.liquid).
   * @param {number} count
   */
  #announce(count) {
    const status = this.refs.paginationStatus;
    if (status instanceof HTMLElement && count > 0) {
      status.textContent = 'Caricati altri ' + count + ' prodotti';
    }
  }

  /**
   * ARVIMO UX: mostra l'invito ai filtri quando l'utente ha caricato almeno N prodotti,
   * solo se non sta gia' filtrando, i filtri esistono e non l'ha gia' chiuso in sessione.
   */
  #maybeShowScrollHint() {
    const hint = this.refs.scrollHint;
    if (!(hint instanceof HTMLElement) || this.#scrollHintShown) return;

    let dismissed = null;
    try {
      dismissed = sessionStorage.getItem('arv-scroll-hint-dismissed');
    } catch (error) {
      // sessionStorage non disponibile: mostra comunque
    }
    if (dismissed) return;

    // Niente invito se l'utente sta gia' filtrando
    const params = new URLSearchParams(window.location.search);
    for (const key of params.keys()) {
      if (key.startsWith('filter.')) return;
    }

    // Serve un modo per aprire i filtri
    if (!document.querySelector('.facets-toggle__button, [ref="facetsForm"]')) return;

    const threshold = Number(hint.dataset.hintAfter) || 0;
    if (!threshold) return;

    const count = Array.isArray(this.refs.cards) ? this.refs.cards.length : 0;
    if (count < threshold) return;

    this.#scrollHintShown = true;
    hint.hidden = false;
    requestAnimationFrame(() => hint.classList.add('arv-scroll-hint--visible'));
  }

  #hideScrollHint() {
    const hint = this.refs.scrollHint;
    if (hint instanceof HTMLElement) {
      hint.classList.remove('arv-scroll-hint--visible');
      hint.hidden = true;
    }
  }

  /** Chiusura manuale (X): non riproporre per la sessione. Invocato via on:click. */
  dismissScrollHint = () => {
    this.#hideScrollHint();
    try {
      sessionStorage.setItem('arv-scroll-hint-dismissed', '1');
    } catch (error) {
      // niente persistenza, pazienza
    }
  };

  /**
   * CTA dell'invito: apre i filtri della collezione. Invocato via on:click.
   * Mobile: click sul toggle nativo che apre il drawer #filters-drawer.
   * Desktop: scroll morbido alla riga facets e apertura del primo dropdown.
   */
  openFiltersFromHint = () => {
    this.dismissScrollHint();

    const toggle = document.querySelector('.facets-toggle__button');
    if (toggle instanceof HTMLElement && toggle.offsetParent !== null) {
      toggle.click();
      return;
    }

    const facets = document.querySelector('[ref="facetsForm"]');
    if (facets instanceof HTMLElement) {
      facets.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const firstFilter = facets.querySelector('details');
      if (firstFilter instanceof HTMLDetailsElement) {
        setTimeout(() => {
          firstFilter.open = true;
        }, 450);
      }
    }
  };

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;

    // ARVIMO: dedup — pagina gia' in cache o fetch gia' in volo => nessuna nuova richiesta
    if (this.pages.has(pageNumber)) return;
    const existing = this.#inflightFetches.get(pageNumber);
    if (existing) return existing;

    const generation = this.#generation;
    const fetchPromise = (async () => {
      try {
        const pageContent = await sectionRenderer.getSectionHTML(
          this.sectionId,
          true,
          pageInfo.url,
          this.#abortController.signal
        );
        // Scarta le risposte arrivate dopo un cambio filtri/sort
        if (generation === this.#generation) {
          this.pages.set(pageNumber, pageContent);
        }
      } catch (error) {
        // Fetch abortito (cambio filtri) o errore di rete: nessuna cache, il sentinel ritentera'
      } finally {
        this.#inflightFetches.delete(pageNumber);
      }
    })();

    this.#inflightFetches.set(pageNumber, fetchPromise);
    return fetchPromise;
  }

  async #renderNextPage() {
    // ARVIMO: request lock — un solo render/fetch 'next' alla volta
    if (this.#renderingNext) return;
    this.#renderingNext = true;

    try {
      const { grid } = this.refs;

      if (!grid) return;

      const nextPage = this.#getPage('next');

      if (!nextPage || !this.#shouldUsePage(nextPage)) return;

      const generation = this.#generation;
      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        this.#showSkeletons();

        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('next');

        await promise;
        this.#hideSkeletons();

        if (generation !== this.#generation) return;

        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements) {
          // Errore di rete o risposta non valida: ritenta tra 3s
          this.#rearmSentinel('next', 3000);
          return;
        }
      }

      // Inserimento in un'unica operazione (append multiplo = un solo aggiornamento del DOM)
      grid.append(...nextPageItemElements);

      this.#aspectRatioHelper?.processNewElements();
      this.#announce(nextPageItemElements.length);
      this.#maybeShowScrollHint();

      await yieldToMainThread();

      if (generation === this.#generation) {
        history.pushState('', '', nextPage.url.toString());
      }

      requestIdleCallback(() => {
        this.#fetchPage('next');
      });

      // Con batch da 12 il sentinel puo' essere ancora dentro il rootMargin: ri-valuta
      this.#rearmSentinel('next');
    } finally {
      this.#renderingNext = false;
      this.#hideSkeletons();
    }
  }

  async #renderPreviousPage() {
    if (this.#renderingPrevious) return;
    this.#renderingPrevious = true;

    try {
      const { grid } = this.refs;

      if (!grid) return;

      const previousPage = this.#getPage('previous');
      if (!previousPage || !this.#shouldUsePage(previousPage)) return;

      const generation = this.#generation;
      let previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolvePreviousPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('previous');

        await promise;

        if (generation !== this.#generation) return;

        previousPageItemElements = this.#getGridForPage(previousPage.page);
        if (!previousPageItemElements) {
          this.#rearmSentinel('previous', 3000);
          return;
        }
      }

      // Store the current scroll position and height of the first element
      const currentScrollTop = getScrollTop();
      const firstElement = grid.firstElementChild;
      const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + currentScrollTop : 0;

      // Prepend the new elements
      grid.prepend(...previousPageItemElements);

      this.#aspectRatioHelper?.processNewElements();

      // Calculate and adjust scroll position to maintain the same view
      if (firstElement) {
        const newHeight = firstElement.getBoundingClientRect().top + getScrollTop();
        const heightDiff = newHeight - oldHeight;
        scrollTo({
          top: currentScrollTop + heightDiff,
          behavior: 'instant',
        });
      }

      await yieldToMainThread();

      if (generation === this.#generation) {
        history.pushState('', '', previousPage.url.toString());
      }

      requestIdleCallback(() => {
        this.#fetchPage('previous');
      });

      this.#rearmSentinel('previous');
    } finally {
      this.#renderingPrevious = false;
    }
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;

    // ARVIMO: invalida le risposte in volo e annulla i fetch dell'infinite scroll
    this.#generation += 1;
    this.#abortController.abort();
    this.#abortController = new AbortController();
    this.#inflightFetches.clear();
    this.#hideSkeletons();
    this.#hideScrollHint();

    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
